<template>
  <div class="container-fluid py-4">

    <!-- Header moderne -->
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-capsule fs-2 text-primary"></i>
        <h2 class="fw-bold mb-0">Détails du Médicament</h2>
      </div>
      <router-link class="btn btn-primary btn-lg shadow-sm btn-clinique mt-2 mt-md-0" :to="{ name: 'liste_medicaments' }">
        <i class="bi bi-arrow-left-circle me-1"></i> Retour
      </router-link>
    </div>

    <!-- Informations médicament -->
    <div class="card p-4 shadow-sm rounded card-clinique mb-4">
      <h5 class="fw-bold text-primary border-bottom pb-2 mb-3">
        <i class="bi bi-capsule me-2"></i> Informations sur le Médicament
      </h5>
      <div class="row g-4">
        <div class="col-md-4">
          <span class="fw-semibold text-muted">Nom :</span>
          <div class="fs-5 text-dark">{{ medicament.nom }}</div>
        </div>
        <div class="col-md-4">
          <span class="fw-semibold text-muted">Stock Actuel :</span>
          <div class="fs-5 text-success">{{ medicament.stockActuel }}</div>
        </div>
        <div class="col-md-4">
          <span class="fw-semibold text-muted">Description :</span>
          <div class="fs-6 text-secondary">{{ medicament.description || 'Aucune' }}</div>
        </div>
      </div>
    </div>

    <!-- Barre de filtre -->
    <div class="card p-3 mb-4 shadow-sm rounded card-clinique">
      <div class="row g-3 align-items-end">
        <div class="col-12 col-md-3">
          <label class="form-label fw-semibold">Rechercher</label>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Rechercher un lot"
            class="form-control form-control-clinique"
          >
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold">Items par page</label>
          <select v-model="itemsPerPage" class="form-select form-select-clinique">
            <option v-for="n in itemsPerPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tableau approvisionnements -->
    <div class="card shadow-sm rounded card-clinique">
      <div class="table-responsive overflow-auto">
        <table class="table align-middle text-center mb-0 table-hover modern-table-clinique w-100">
          <thead>
            <tr>
              <th>#</th>
              <th>Date d'ajout</th>
              <th>Quantité</th>
              <th>Conditionnement</th>
              <th>Prix Achat</th>
              <th>Prix Vente</th>
              <th>Total Achat</th>
              <th>Total Vente</th>
              <th>Date péremption</th>
              <th>N° Lot</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(appro, index) in paginatedApprovisionnements"
              :key="appro.id"
              :class="getExpirationClass(appro.datePeremption)"
            >
              <td>{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
              <td>{{ formatDate(appro.dateApprovisionnement) }}</td>
              <td>{{ appro.quantite }}</td>
              <td>{{ appro.conditionnement?.nom }}</td>
              <td>{{ formatPrice(appro.prixAchat) }}</td>
              <td>{{ formatPrice(appro.prixVente) }}</td>
              <td>{{ formatPrice(appro.quantite * appro.prixAchat) }}</td>
              <td>{{ formatPrice(appro.quantite * appro.prixVente) }}</td>
              <td>{{ formatDate(appro.datePeremption) }}</td>
              <td>{{ appro.numeroLot || '-' }}</td>
            </tr>
            <tr v-if="filteredApprovisionnements.length === 0">
              <td colspan="10" class="text-center py-3">Aucune donnée disponible</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination moderne -->
      <div class="d-flex justify-content-end p-3">
        <ul class="pagination pagination-modern flex-wrap mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(currentPage - 1)">Précédent</button>
          </li>
          <li
            v-for="page in totalPages"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page }"
          >
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="changePage(currentPage + 1)">Suivant</button>
          </li>
        </ul>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/api';
import dayjs from 'dayjs';

export default {
  name: 'DetailsMedicament',
  data() {
    return {
      medicament: {},
      approvisionnements: [],
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 50,
      itemsPerPageOptions: [50, 100, 200, 400]
    };
  },
  computed: {
    filteredApprovisionnements() {
      return this.approvisionnements
        .filter(a => !a.isDeleted)
        .filter(a => {
          const q = this.searchQuery.toLowerCase();
          return (
            a.numeroLot?.toLowerCase().includes(q) ||
            a.conditionnement?.nom?.toLowerCase().includes(q)
          );
        });
    },
    totalPages() {
      return Math.ceil(this.filteredApprovisionnements.length / this.itemsPerPage);
    },
    paginatedApprovisionnements() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredApprovisionnements.slice(start, start + this.itemsPerPage);
    }
  },
  watch: {
    searchQuery() { this.currentPage = 1; },
    itemsPerPage() { this.currentPage = 1; }
  },
  methods: {
    changePage(page) { if (page > 0 && page <= this.totalPages) this.currentPage = page; },
    formatDate(date) { return dayjs(date).format('DD-MM-YYYY'); },
    formatPrice(value) {
      if (value == null) return '';
      const parsed = parseFloat(value);
      return parsed.toLocaleString('fr-FR', { minimumFractionDigits: parsed % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
    },
    getExpirationClass(date) {
      if (!date) return '';
      const today = dayjs();
      const expirationDate = dayjs(date);
      if (expirationDate.isBefore(today, 'day')) return 'table-danger';
      const diffInMonths = expirationDate.diff(today, 'month', true);
      if (diffInMonths <= 3) return 'table-warning';
      return '';
    },
    async loadData() {
      const id = parseInt(this.$route.params.id);
      try {
        const response = await api.getItems('medicaments'); 
        const medicaments = response.data.data || response.data;
        const selected = medicaments.find(m => m.id === id);
        if (selected) {
          selected.approvisionnements = selected.approvisionnements.filter(a => !a.isDeleted);
          this.medicament = selected;
          this.approvisionnements = selected.approvisionnements;
        }
      } catch (err) {
        console.error('Erreur lors du chargement du médicament', err);
      }
    }
  },
  created() { this.loadData(); }
};
</script>

<style scoped>
/* --- Header moderne --- */
.header-clinique {
  padding: 15px 20px;
  background-color: #e9f5ff;
  border-radius: 12px;
  margin-bottom: 20px;
}

/* --- Card design --- */
.card-clinique {
  background: #f8fafd;
  border-radius: 12px;
  border: none;
}

/* --- Champs --- */
.form-control-clinique,
.form-select-clinique {
  border-radius: 10px;
  border: 1px solid #d1e3f0;
  padding: 8px 12px;
  transition: all 0.3s ease;
}
.form-control-clinique:focus,
.form-select-clinique:focus {
  box-shadow: 0 0 6px #0d6efd33;
  border-color: #0d6efd;
}

/* --- Boutons --- */
.btn-clinique {
  background: linear-gradient(90deg, #0d6efd, #6610f2);
  border: none;
  color: #fff;
  border-radius: 10px;
  transition: all 0.3s ease;
}
.btn-clinique:hover {
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  transform: translateY(-2px);
}

/* --- Tableau moderne --- */
.modern-table-clinique thead {
  background: linear-gradient(90deg, #8fd3f4, #0d6efd);
  color: #fff;
  font-weight: 600;
}
.modern-table-clinique tbody tr:hover {
  background-color: #e1f0ff;
  cursor: pointer;
  transition: all 0.3s ease;
}
.table td, .table th {
  vertical-align: middle;
  text-align: center;
}

/* --- Pagination pastille --- */
.pagination-modern .page-link {
  border-radius: 50% !important;
  margin: 0 4px;
  min-width: 38px;
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0d6efd;
  border: 1px solid #dee2e6;
}
.pagination-modern .page-item.active .page-link {
  background-color: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}
.pagination-modern .page-link:hover {
  background-color: #d1e7ff;
  transition: all 0.3s ease;
}
</style>
